import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { firstValueFrom } from 'rxjs';
import {
  ControlQuestionService,
  ControlQuestionSurvey,
  ControlQuestion,
  ControlQuestionOption,
  ControlQuestionLink
} from '../../services/controlquestion.service';
import { UserSessionService } from '../../services/user-session.service';

type QuestionTypeKey =
  | 'SINGLE_CHOICE'
  | 'MULTIPLE_CHOICE'
  | 'IMAGE_CHOICE'
  | 'TEXT'
  | 'EMAIL'
  | 'NUMBER'
  | 'DATE'
  | 'MATRIX'
  | 'RANKING'
  | 'DROPDOWN'
  | 'EMOJI'
  | 'STAR'
  | 'SEMANTIC'
  | 'SCALE'
  | 'NPS'
  | 'CUSTOM_TEXT';

interface QuestionTypeDef {
  key: QuestionTypeKey;
  label: string;
  group: string;
  icon: string;
  enabled: boolean;
}

interface OptionDraft {
  id?: number;
  label: string;
  is_other?: boolean;
  is_correct?: boolean;
  image_url?: string | null;
}

interface MatrixItemDraft {
  id?: number;
  label: string;
}

@Component({
  selector: 'app-controlquestion',
  templateUrl: './controlquestion.component.html',
  styleUrls: ['./controlquestion.component.scss']
})
export class ControlquestionComponent implements OnInit {
  tenantId: number | null = null;
  userId: number | null = null;

  surveys: ControlQuestionSurvey[] = [];
  activeSurvey: ControlQuestionSurvey | null = null;
  questions: ControlQuestion[] = [];
  options: ControlQuestionOption[] = [];
  matrixRows: any[] = [];
  matrixCols: any[] = [];

  searchTerm = '';
  statusFilter: 'ALL' | 'DRAFT' | 'PUBLISHED' | 'ARCHIVED' | 'EXPIRED' = 'ALL';

  showSurveyModal = false;
  showQuestionTypeModal = false;
  isSelectingQuestionType = false;
  showLinkModal = false;
  showPreviewModal = false;
  showMetricsModal = false;
  editingSurveyId: number | null = null;
  surveyEditTab: 'survey' | 'link' = 'survey';
  openSurveyMenuId: number | null = null;
  openStatusMenuId: number | null = null;
  copiedSurveyId: number | null = null;
  copiedSurveyMessage = '';
  copiedSurveyTimer: ReturnType<typeof setTimeout> | null = null;
  linkCopied = false;
  linkCopiedTimer: ReturnType<typeof setTimeout> | null = null;
  metricsLoading = false;
  metricsError = '';
  surveyMetrics: any = null;
  metricsSurvey: ControlQuestionSurvey | null = null;

  surveyForm = {
    title: '',
    description: '',
    intro_text: '',
    outro_text: '',
    status: 'DRAFT',
    intranet_enabled: false
  };

  questionForm = {
    title: '',
    description: '',
    question_type: 'SINGLE_CHOICE' as QuestionTypeKey,
    media_type: 'NONE',
    media_url: '',
    display_format: 'ROW',
    min_answers: 1,
    max_answers: 1,
    required: true,
    weight: 0
  };

  optionDrafts: OptionDraft[] = [];
  matrixRowDrafts: MatrixItemDraft[] = [];
  matrixColDrafts: MatrixItemDraft[] = [];
  editingQuestionId: number | null = null;
  showQuestionForm = false;
  activeOptionImageIndex: number | null = null;

  linkForm = {
    status: 'PUBLISHED',
    password: '',
    expires_at: '',
    max_responses: '',
    allowed_ips: '',
    allow_anonymous: true
  };
  activeLink: ControlQuestionLink | null = null;
  linkSurvey: ControlQuestionSurvey | null = null;
  editingLink = false;
  linkHasPassword = false;
  removeLinkPassword = false;
  linkPasswordVisible = false;
  private readonly maskedPassword = '***';
  linkPasswordCache = '';
  generatedLink = '';
  previewFrameUrl: SafeResourceUrl | null = null;
  previewAnswers: Record<number, any> = {};
  previewTouched: Record<number, boolean> = {};

  @ViewChild('questionImageInput') questionImageInput?: ElementRef<HTMLInputElement>;
  @ViewChild('questionVideoInput') questionVideoInput?: ElementRef<HTMLInputElement>;

  questionTypes: QuestionTypeDef[] = [
    { key: 'SINGLE_CHOICE', label: 'Escolha única', group: 'Básico', icon: 'check_circle', enabled: true },
    { key: 'MULTIPLE_CHOICE', label: 'Escolha múltipla', group: 'Básico', icon: 'done_all', enabled: true },
    { key: 'IMAGE_CHOICE', label: 'Escolha com imagens', group: 'Básico', icon: 'image', enabled: true },
    { key: 'TEXT', label: 'Texto de resposta', group: 'Aberto', icon: 'text_fields', enabled: true },
    { key: 'EMAIL', label: 'Resposta de e-mail', group: 'Aberto', icon: 'mail', enabled: false },
    { key: 'NUMBER', label: 'Resposta numérica', group: 'Aberto', icon: 'numbers', enabled: true },
    { key: 'DATE', label: 'Resposta com data', group: 'Aberto', icon: 'event', enabled: false },
    { key: 'MATRIX', label: 'Matriz', group: 'Estruturado', icon: 'grid_on', enabled: false },
    { key: 'RANKING', label: 'Ordem de preferência', group: 'Estruturado', icon: 'swap_vert', enabled: false },
    { key: 'DROPDOWN', label: 'Lista suspensa', group: 'Estruturado', icon: 'arrow_drop_down_circle', enabled: false },
    { key: 'EMOJI', label: 'Avaliação emoji', group: 'Avaliativo', icon: 'sentiment_satisfied', enabled: true },
    { key: 'STAR', label: 'Avaliação de estrelas', group: 'Avaliativo', icon: 'star', enabled: true },
    { key: 'SEMANTIC', label: 'Diferencial semântico', group: 'Avaliativo', icon: 'tune', enabled: false },
    { key: 'SCALE', label: 'Avaliação de número', group: 'Avaliativo', icon: 'linear_scale', enabled: true },
    { key: 'NPS', label: 'Net Promoter Score', group: 'Premium', icon: 'trending_up', enabled: false },
    { key: 'CUSTOM_TEXT', label: 'Texto personalizado', group: 'Elementos', icon: 'notes', enabled: false }
  ];

  displayFormats = [
    { value: 'ROW', label: 'Em uma fileira' },
    { value: 'GRID', label: 'Em grade' },
    { value: 'COLUMN', label: 'Em coluna' }
  ];

  get questionTypeGroups() {
    const grouped = new Map<string, QuestionTypeDef[]>();
    this.questionTypes.forEach((type) => {
      if (!grouped.has(type.group)) {
        grouped.set(type.group, []);
      }
      grouped.get(type.group)?.push(type);
    });
    return Array.from(grouped.entries()).map(([name, items]) => ({ name, items }));
  }

  constructor(
    private controlquestion: ControlQuestionService,
    private session: UserSessionService,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit() {
    const user = this.session.getUser();
    this.tenantId = user?.tenant_id || null;
    this.userId = user?.id || null;
    this.loadSurveys();
  }

  get filteredSurveys() {
    const term = this.searchTerm.trim().toLowerCase();
    return this.surveys.filter((survey) => {
      if (this.statusFilter !== 'ALL' && this.surveyDisplayStatus(survey) !== this.statusFilter) {
        return false;
      }
      if (!term) {
        return true;
      }
      return survey.title.toLowerCase().includes(term);
    });
  }

  openSurvey(survey: ControlQuestionSurvey) {
    this.activeSurvey = survey;
    this.openSurveyMenuId = null;
    this.openStatusMenuId = null;
    this.showMetricsModal = false;
    this.loadSurveyDetail(survey.id);
  }

  closeSurvey() {
    this.activeSurvey = null;
    this.questions = [];
    this.options = [];
    this.matrixRows = [];
    this.matrixCols = [];
    this.showQuestionForm = false;
    this.openStatusMenuId = null;
    this.showMetricsModal = false;
  }

  loadSurveys() {
    if (!this.tenantId) {
      return;
    }
    const search = this.searchTerm.trim();
    const filters: Record<string, string> = {};
    if (search) {
      filters['search'] = search;
    }
    if (this.statusFilter !== 'ALL') {
      filters['status'] = this.statusFilter;
    }
    this.controlquestion.listSurveys(this.tenantId, filters).subscribe((rows) => (this.surveys = rows || []));
  }

  loadSurveyDetail(surveyId: number) {
    if (!this.tenantId) {
      return;
    }
    this.controlquestion.getSurvey(this.tenantId, surveyId).subscribe((detail) => {
      this.activeSurvey = detail.survey;
      this.questions = detail.questions || [];
      this.options = detail.options || [];
      this.matrixRows = detail.matrixRows || [];
      this.matrixCols = detail.matrixCols || [];
      const keepEditorState = this.showQuestionTypeModal || this.isSelectingQuestionType;
      if (!keepEditorState) {
        this.showQuestionForm = this.questions.length > 0;
        this.resetQuestionForm();
      }
    });
  }

  openSurveyModal() {
    this.editingSurveyId = null;
    this.surveyEditTab = 'survey';
    this.surveyForm = {
      title: '',
      description: '',
      intro_text: '',
      outro_text: '',
      status: 'DRAFT',
      intranet_enabled: false
    };
    this.showSurveyModal = true;
  }

  openSurveyEdit(survey: ControlQuestionSurvey, event?: Event) {
    event?.stopPropagation();
    this.editingSurveyId = survey.id;
    this.surveyEditTab = 'survey';
    this.linkSurvey = survey;
    this.loadLinkForSurvey(survey.id);
    this.removeLinkPassword = false;
    this.surveyForm = {
      title: survey.title || '',
      description: survey.description || '',
      intro_text: survey.intro_text || '',
      outro_text: survey.outro_text || '',
      status: survey.status || 'DRAFT',
      intranet_enabled: !!survey.intranet_enabled
    };
    this.showSurveyModal = true;
    this.openSurveyMenuId = null;
  }

  toggleSurveyMenu(surveyId: number, event?: Event) {
    event?.stopPropagation();
    this.openSurveyMenuId = this.openSurveyMenuId === surveyId ? null : surveyId;
    this.openStatusMenuId = null;
  }

  toggleStatusMenu(surveyId: number, event?: Event) {
    event?.stopPropagation();
    this.openStatusMenuId = this.openStatusMenuId === surveyId ? null : surveyId;
    this.openSurveyMenuId = null;
  }

  openMetricsModal(survey: ControlQuestionSurvey, event?: Event) {
    event?.stopPropagation();
    if (!this.tenantId) {
      return;
    }
    this.metricsSurvey = survey;
    this.metricsLoading = true;
    this.metricsError = '';
    this.surveyMetrics = null;
    this.showMetricsModal = true;
    this.controlquestion.getSurveyMetrics(this.tenantId, survey.id).subscribe({
      next: (metrics) => {
        this.surveyMetrics = metrics;
        this.metricsLoading = false;
      },
      error: (err) => {
        this.metricsError = err?.error?.error || 'Não foi possível carregar métricas.';
        this.metricsLoading = false;
      }
    });
  }

  closeMetricsModal() {
    this.showMetricsModal = false;
  }

  maxOptionCount(options: Array<{ count: number }>) {
    if (!options?.length) {
      return 1;
    }
    return Math.max(1, ...options.map((opt) => Number(opt.count) || 0));
  }

  hasCorrectOption = (option: { is_correct?: boolean | number }) => !!option?.is_correct;

  formatDuration(seconds: number) {
    const total = Number(seconds || 0);
    if (!total || total < 0) {
      return '—';
    }
    const minutes = Math.floor(total / 60);
    const secs = Math.floor(total % 60);
    if (minutes < 60) {
      return `${minutes}m ${String(secs).padStart(2, '0')}s`;
    }
    const hours = Math.floor(minutes / 60);
    const remMinutes = minutes % 60;
    return `${hours}h ${String(remMinutes).padStart(2, '0')}m`;
  }

  setSurveyTab(tab: 'survey' | 'link') {
    this.surveyEditTab = tab;
  }

  saveSurvey() {
    if (!this.tenantId || !this.userId || !this.surveyForm.title.trim()) {
      return;
    }
    const payload = {
      tenant_id: this.tenantId,
      created_by: this.userId,
      updated_by: this.userId,
      title: this.surveyForm.title.trim(),
      description: this.surveyForm.description.trim() || null,
      intro_text: this.surveyForm.intro_text.trim() || null,
      outro_text: this.surveyForm.outro_text.trim() || null,
      status: this.surveyForm.status,
      intranet_enabled: this.surveyForm.intranet_enabled ? 1 : 0
    };
    if (this.editingSurveyId) {
      this.controlquestion.updateSurvey(this.editingSurveyId, payload).subscribe((updated) => {
        this.surveys = this.surveys.map((survey) => (survey.id === updated.id ? updated : survey));
        this.showSurveyModal = false;
        this.editingSurveyId = null;
      });
      return;
    }
    this.controlquestion.createSurvey(payload).subscribe((created) => {
      this.surveys = [created, ...this.surveys];
      this.showSurveyModal = false;
    });
  }

  deleteSurvey(survey: ControlQuestionSurvey, event?: Event) {
    event?.stopPropagation();
    this.openSurveyMenuId = null;
    if (!this.tenantId) {
      return;
    }
    if (!confirm(`Excluir o questionário "${survey.title}"?`)) {
      return;
    }
    this.controlquestion.deleteSurvey(this.tenantId, survey.id).subscribe(() => {
      this.surveys = this.surveys.filter((item) => item.id !== survey.id);
      if (this.activeSurvey?.id === survey.id) {
        this.closeSurvey();
      }
    });
  }

  openQuestionTypeModal() {
    if (!this.activeSurvey || this.isSelectingQuestionType) {
      return;
    }
    this.showQuestionTypeModal = true;
  }

  closeQuestionTypeModal() {
    this.showQuestionTypeModal = false;
    this.isSelectingQuestionType = false;
  }

  selectQuestionType(type: QuestionTypeDef | null | undefined, event?: Event) {
    event?.preventDefault();
    event?.stopPropagation();
    if (!type?.key || !this.activeSurvey) {
      return;
    }
    if (!type.enabled) {
      return;
    }
    if (this.isSelectingQuestionType) {
      return;
    }
    this.isSelectingQuestionType = true;
    const selectedType = type.key;
    this.showQuestionTypeModal = false;
    this.editingQuestionId = null;
    this.questionForm = {
      title: '',
      description: '',
      question_type: selectedType,
      media_type: 'NONE',
      media_url: '',
      display_format: 'ROW',
      min_answers: 1,
      max_answers: 1,
      required: true,
      weight: 0
    };
    this.optionDrafts = [];
    this.matrixRowDrafts = [];
    this.matrixColDrafts = [];
    this.activeOptionImageIndex = null;
    this.applyDefaultOptionsForType(selectedType);
    setTimeout(() => {
      this.showQuestionForm = true;
      // Avoid rebound click reopening the type modal on the same interaction.
      setTimeout(() => (this.isSelectingQuestionType = false), 120);
    }, 0);
  }

  selectQuestion(question: ControlQuestion) {
    this.showQuestionForm = true;
    this.editingQuestionId = question.id;
    this.questionForm = {
      title: question.title || '',
      description: question.description || '',
      question_type: question.question_type as QuestionTypeKey,
      media_type: question.media_type || 'NONE',
      media_url: question.media_url || '',
      display_format: question.display_format || 'ROW',
      min_answers: question.min_answers ?? 1,
      max_answers: question.max_answers ?? 1,
      required: !!question.required,
      weight: Number(question.weight || 0)
    };
    this.optionDrafts = this.options
      .filter((opt) => opt.question_id === question.id)
      .map((opt) => ({
        id: opt.id,
        label: opt.label,
        is_other: !!opt.is_other,
        is_correct: !!opt.is_correct,
        image_url: opt.image_url || null
      }));
    this.matrixRowDrafts = this.matrixRows
      .filter((row) => row.question_id === question.id)
      .map((row: any) => ({ id: row.id, label: row.label }));
    this.matrixColDrafts = this.matrixCols
      .filter((col) => col.question_id === question.id)
      .map((col: any) => ({ id: col.id, label: col.label }));
  }

  resetQuestionForm(clearType = true, keepOpen = false) {
    this.editingQuestionId = null;
    this.questionForm = {
      title: '',
      description: '',
      question_type: clearType ? 'SINGLE_CHOICE' : this.questionForm.question_type,
      media_type: 'NONE',
      media_url: '',
      display_format: 'ROW',
      min_answers: 1,
      max_answers: 1,
      required: true,
      weight: 0
    };
    this.optionDrafts = [];
    this.matrixRowDrafts = [];
    this.matrixColDrafts = [];
    this.showQuestionForm = keepOpen || this.questions.length > 0;
  }

  addOption() {
    this.optionDrafts = [...this.optionDrafts, { label: '', image_url: null, is_correct: false }];
  }

  addOtherOption() {
    this.optionDrafts = [...this.optionDrafts, { label: 'Outra', is_other: true, image_url: null, is_correct: false }];
  }

  removeOption(index: number) {
    this.optionDrafts.splice(index, 1);
  }

  supportsCorrectAnswers() {
    return ['SINGLE_CHOICE', 'MULTIPLE_CHOICE', 'IMAGE_CHOICE'].includes(this.questionForm.question_type);
  }

  isSingleCorrectQuestion() {
    return ['SINGLE_CHOICE', 'IMAGE_CHOICE'].includes(this.questionForm.question_type);
  }

  toggleCorrectOption(index: number) {
    if (!this.supportsCorrectAnswers()) {
      return;
    }
    const next = [...this.optionDrafts];
    const target = next[index];
    if (!target || target.is_other) {
      return;
    }
    if (this.isSingleCorrectQuestion()) {
      next.forEach((option, i) => {
        option.is_correct = i === index ? !target.is_correct : false;
      });
    } else {
      target.is_correct = !target.is_correct;
    }
    this.optionDrafts = next;
  }

  addMatrixRow() {
    this.matrixRowDrafts = [...this.matrixRowDrafts, { label: '' }];
  }

  addMatrixCol() {
    this.matrixColDrafts = [...this.matrixColDrafts, { label: '' }];
  }

  removeMatrixRow(index: number) {
    this.matrixRowDrafts.splice(index, 1);
  }

  removeMatrixCol(index: number) {
    this.matrixColDrafts.splice(index, 1);
  }

  async saveQuestion() {
    if (!this.tenantId || !this.userId || !this.activeSurvey) {
      return;
    }
    if (!this.questionForm.title.trim()) {
      return;
    }
    const payload = {
      tenant_id: this.tenantId,
      created_by: this.userId,
      question_type: this.questionForm.question_type,
      title: this.questionForm.title.trim(),
      description: this.questionForm.description.trim() || null,
      media_type: this.questionForm.media_type,
      media_url: this.questionForm.media_url.trim() || null,
      weight: this.questionForm.weight || 0,
      required: this.questionForm.required ? 1 : 0,
      display_format: this.questionForm.display_format,
      min_answers: this.questionForm.min_answers || 0,
      max_answers: this.questionForm.max_answers || 1,
      order_index: this.questions.length
    };
    if (this.editingQuestionId) {
      await firstValueFrom(this.controlquestion.updateQuestion(this.editingQuestionId, payload));
    } else {
      const created = await firstValueFrom(this.controlquestion.createQuestion(this.activeSurvey.id, payload));
      this.editingQuestionId = created.id;
    }
    await this.syncQuestionDetail();
    this.loadSurveyDetail(this.activeSurvey.id);
  }

  async syncQuestionDetail() {
    if (!this.tenantId || !this.editingQuestionId) {
      return;
    }
    const questionId = this.editingQuestionId;
    const existing = this.options.filter((opt) => opt.question_id === questionId);
    const existingRows = this.matrixRows.filter((row: any) => row.question_id === questionId);
    const existingCols = this.matrixCols.filter((col: any) => col.question_id === questionId);

    const normalizedOptionDrafts = this.normalizeCorrectAnswersByType(
      this.questionForm.question_type as QuestionTypeKey,
      this.normalizeOptionDraftsByType(
        this.questionForm.question_type as QuestionTypeKey,
        this.optionDrafts
      )
    );
    this.optionDrafts = [...normalizedOptionDrafts];

    const optionPromises: Array<Promise<unknown>> = [];
    const nextOptionIds = new Set<number>();
    normalizedOptionDrafts.forEach((opt, index) => {
      if (!opt.label.trim()) {
        return;
      }
      const payload = {
        tenant_id: this.tenantId,
        label: opt.label.trim(),
        is_correct: opt.is_correct ? 1 : 0,
        is_other: opt.is_other ? 1 : 0,
        order_index: index,
        image_url: opt.image_url || null
      };
      if (opt.id) {
        nextOptionIds.add(opt.id);
        optionPromises.push(firstValueFrom(this.controlquestion.updateOption(opt.id, payload)));
      } else {
        optionPromises.push(firstValueFrom(this.controlquestion.createOption(questionId, payload)));
      }
    });
    existing.forEach((opt) => {
      if (!nextOptionIds.has(opt.id)) {
        optionPromises.push(firstValueFrom(this.controlquestion.deleteOption(this.tenantId!, opt.id)));
      }
    });

    const rowPromises: Array<Promise<unknown>> = [];
    const nextRowIds = new Set<number>();
    this.matrixRowDrafts.forEach((row, index) => {
      if (!row.label.trim()) {
        return;
      }
      const payload = { tenant_id: this.tenantId, label: row.label.trim(), order_index: index };
      if (row.id) {
        nextRowIds.add(row.id);
        rowPromises.push(firstValueFrom(this.controlquestion.updateMatrixRow(row.id, payload)));
      } else {
        rowPromises.push(firstValueFrom(this.controlquestion.createMatrixRow(questionId, payload)));
      }
    });
    existingRows.forEach((row: any) => {
      if (!nextRowIds.has(row.id)) {
        rowPromises.push(firstValueFrom(this.controlquestion.deleteMatrixRow(this.tenantId!, row.id)));
      }
    });

    const colPromises: Array<Promise<unknown>> = [];
    const nextColIds = new Set<number>();
    this.matrixColDrafts.forEach((col, index) => {
      if (!col.label.trim()) {
        return;
      }
      const payload = { tenant_id: this.tenantId, label: col.label.trim(), order_index: index };
      if (col.id) {
        nextColIds.add(col.id);
        colPromises.push(firstValueFrom(this.controlquestion.updateMatrixCol(col.id, payload)));
      } else {
        colPromises.push(firstValueFrom(this.controlquestion.createMatrixCol(questionId, payload)));
      }
    });
    existingCols.forEach((col: any) => {
      if (!nextColIds.has(col.id)) {
        colPromises.push(firstValueFrom(this.controlquestion.deleteMatrixCol(this.tenantId!, col.id)));
      }
    });

    await Promise.all([...optionPromises, ...rowPromises, ...colPromises]);
  }

  deleteQuestion(question: ControlQuestion) {
    if (!this.tenantId) {
      return;
    }
    if (!confirm('Excluir esta pergunta?')) {
      return;
    }
    this.controlquestion.deleteQuestion(this.tenantId, question.id).subscribe(() => {
      this.questions = this.questions.filter((item) => item.id !== question.id);
      this.options = this.options.filter((opt) => opt.question_id !== question.id);
      this.matrixRows = this.matrixRows.filter((row: any) => row.question_id !== question.id);
      this.matrixCols = this.matrixCols.filter((col: any) => col.question_id !== question.id);
      if (this.editingQuestionId === question.id) {
        this.resetQuestionForm();
      }
      if (!this.questions.length && !this.editingQuestionId) {
        this.showQuestionForm = false;
      }
    });
  }

  questionTypeLabel(key: string) {
    return this.questionTypes.find((type) => type.key === key)?.label || key;
  }

  questionSupportsOptions() {
    return ['SINGLE_CHOICE', 'MULTIPLE_CHOICE', 'IMAGE_CHOICE', 'DROPDOWN', 'RANKING', 'EMOJI', 'STAR', 'SCALE'].includes(
      this.questionForm.question_type
    );
  }

  isQuestionTypeEnabled(typeKey: QuestionTypeKey | string) {
    return !!this.questionTypes.find((type) => type.key === typeKey)?.enabled;
  }

  canSelectQuestionType(type: QuestionTypeDef) {
    return !!type?.enabled;
  }

  onQuestionTypeChange() {
    if (!this.isQuestionTypeEnabled(this.questionForm.question_type)) {
      this.questionForm.question_type = 'SINGLE_CHOICE';
    }
    this.applyDefaultOptionsForType(this.questionForm.question_type);
  }

  private applyDefaultOptionsForType(type: QuestionTypeKey) {
    if (type === 'EMOJI' && this.optionDrafts.length === 0) {
      this.optionDrafts = [
        { label: '😀 Excelente', is_correct: false },
        { label: '🙂 Bom', is_correct: false },
        { label: '😐 Neutro', is_correct: false },
        { label: '🙁 Ruim', is_correct: false },
        { label: '😡 Péssimo', is_correct: false }
      ];
      return;
    }
    if (type === 'STAR' && this.optionDrafts.length === 0) {
      this.optionDrafts = [
        { label: '1 estrela', is_correct: false },
        { label: '2 estrelas', is_correct: false },
        { label: '3 estrelas', is_correct: false },
        { label: '4 estrelas', is_correct: false },
        { label: '5 estrelas', is_correct: false }
      ];
      return;
    }
    if (type === 'SCALE' && this.optionDrafts.length === 0) {
      this.optionDrafts = [
        { label: '1', is_correct: false },
        { label: '2', is_correct: false },
        { label: '3', is_correct: false },
        { label: '4', is_correct: false },
        { label: '5', is_correct: false }
      ];
      this.questionForm.min_answers = 1;
      this.questionForm.max_answers = 1;
    }
  }

  private normalizeOptionDraftsByType(type: QuestionTypeKey, drafts: OptionDraft[]) {
    if (type === 'SCALE') {
      const byValue = new Map<number, OptionDraft>();
      drafts.forEach((draft) => {
        const value = this.getOptionNumericValue(draft);
        if (value !== null && value >= 1 && value <= 5) {
          byValue.set(value, draft);
        }
      });
      return [1, 2, 3, 4, 5].map((value) => ({
        ...(byValue.get(value) || {}),
        label: String(value),
        is_correct: false,
        is_other: false,
        image_url: null
      }));
    }

    if (type === 'STAR') {
      const byValue = new Map<number, OptionDraft>();
      drafts.forEach((draft) => {
        const value = this.getStarCount(draft);
        if (value >= 1 && value <= 5) {
          byValue.set(value, draft);
        }
      });
      return [1, 2, 3, 4, 5].map((value) => ({
        ...(byValue.get(value) || {}),
        label: `${value} estrela${value > 1 ? 's' : ''}`,
        is_correct: false,
        is_other: false,
        image_url: null
      }));
    }

    return drafts;
  }

  private normalizeCorrectAnswersByType(type: QuestionTypeKey, drafts: OptionDraft[]) {
    const allowsCorrect = ['SINGLE_CHOICE', 'MULTIPLE_CHOICE', 'IMAGE_CHOICE'].includes(type);
    if (!allowsCorrect) {
      return drafts.map((draft) => ({ ...draft, is_correct: false }));
    }

    const next = drafts.map((draft) => ({
      ...draft,
      is_correct: !!draft.is_correct && !draft.is_other
    }));

    if (['SINGLE_CHOICE', 'IMAGE_CHOICE'].includes(type)) {
      let found = false;
      return next.map((draft) => {
        if (!draft.is_correct || found) {
          return { ...draft, is_correct: false };
        }
        found = true;
        return draft;
      });
    }

    return next;
  }

  getOptionNumericValue(option: { label?: string | null }) {
    const raw = String(option?.label || '').trim();
    const parsed = Number.parseInt(raw, 10);
    return Number.isFinite(parsed) ? parsed : null;
  }

  getStarCount(option: { label?: string | null }) {
    const numeric = this.getOptionNumericValue(option);
    if (numeric !== null) {
      return Math.max(1, Math.min(5, numeric));
    }
    const match = String(option?.label || '').match(/(\d+)/);
    if (!match) {
      return 1;
    }
    return Math.max(1, Math.min(5, Number.parseInt(match[1], 10)));
  }

  getStarArray(option: { label?: string | null }) {
    return Array.from({ length: this.getStarCount(option) });
  }

  questionSupportsMatrix() {
    return this.questionForm.question_type === 'MATRIX';
  }

  openLinkModal(survey?: ControlQuestionSurvey, event?: Event) {
    event?.stopPropagation();
    const targetSurvey = survey || this.activeSurvey;
    if (!targetSurvey) {
      return;
    }
    this.linkSurvey = targetSurvey;
    this.activeLink = null;
    this.editingLink = false;
    this.linkHasPassword = false;
    this.removeLinkPassword = false;
    this.linkPasswordVisible = false;
    this.linkPasswordCache = '';
    this.generatedLink = '';
    this.linkForm = {
      status: 'PUBLISHED',
      password: '',
      expires_at: '',
      max_responses: '',
      allowed_ips: '',
      allow_anonymous: true
    };
    this.loadLinkForSurvey(targetSurvey.id);
    this.showLinkModal = true;
  }

  async createLink() {
    if (!this.tenantId || !this.userId || !this.linkSurvey) {
      return;
    }
    const tenantId = this.tenantId;
    const userId = this.userId;
    const surveyId = this.linkSurvey.id;
    const canGenerate = await this.ensureSurveyHasQuestions(surveyId);
    if (!canGenerate) {
      return;
    }
    const allowed_ips = (this.linkForm.allowed_ips || '')
      .split(',')
      .map((ip) => ip.trim())
      .filter(Boolean);
    const payload = {
      tenant_id: tenantId,
      created_by: userId,
      survey_id: surveyId,
      status: this.linkForm.status,
      password: this.linkForm.password || null,
      expires_at: this.linkForm.expires_at || null,
      max_responses: this.linkForm.max_responses ? Number(this.linkForm.max_responses) : null,
      allow_anonymous: this.linkForm.allow_anonymous,
      allowed_ips
    };
    this.controlquestion.createLink(payload).subscribe((response) => {
      this.generatedLink = `${window.location.origin}/controlquestion/public/${response.token}`;
      this.activeLink = {
        id: response.id,
        tenant_id: tenantId,
        survey_id: surveyId,
        token: response.token,
        status: this.linkForm.status,
        expires_at: this.linkForm.expires_at || null,
        max_responses: this.linkForm.max_responses ? Number(this.linkForm.max_responses) : null,
        allow_anonymous: this.linkForm.allow_anonymous ? 1 : 0
      };
      this.linkHasPassword = !!this.linkForm.password;
      this.linkPasswordCache = this.linkForm.password || '';
      this.persistLinkPassword(response.id, this.linkPasswordCache);
      this.removeLinkPassword = false;
      this.linkPasswordVisible = false;
      this.editingLink = false;
      this.loadSurveys();
    });
  }

  loadLinkForSurvey(surveyId: number) {
    if (!this.tenantId) {
      return;
    }
    this.controlquestion.listLinks(this.tenantId, surveyId).subscribe((links) => {
      const link = links?.[0] || null;
      if (!link) {
        this.activeLink = null;
        this.linkHasPassword = false;
        this.removeLinkPassword = false;
        this.linkPasswordVisible = false;
        this.linkPasswordCache = '';
        return;
      }
      this.activeLink = link;
      this.generatedLink = `${window.location.origin}/controlquestion/public/${link.token}`;
      this.linkForm = {
        status: link.status || 'PUBLISHED',
        password: '',
        expires_at: link.expires_at ? this.formatDateTimeLocal(link.expires_at) : '',
        max_responses: link.max_responses ? String(link.max_responses) : '',
        allowed_ips: (link.allowed_ips || []).join(', '),
        allow_anonymous: !!link.allow_anonymous
      };
      this.linkHasPassword = !!link.has_password;
      if (this.linkHasPassword) {
        const serverPassword = String(link.password_preview || '').trim();
        const cachedPassword = this.readPersistedLinkPassword(link.id);
        this.linkPasswordCache = serverPassword || cachedPassword;
        if (this.linkPasswordCache) {
          this.persistLinkPassword(link.id, this.linkPasswordCache);
        }
      } else {
        this.linkPasswordCache = '';
      }
      this.removeLinkPassword = false;
      this.linkPasswordVisible = false;
    });
  }

  startEditLink() {
    if (!this.activeLink) {
      return;
    }
    this.editingLink = true;
    this.generatedLink = '';
  }

  saveLinkEdits() {
    if (!this.tenantId || !this.activeLink) {
      return;
    }
    const allowed_ips = (this.linkForm.allowed_ips || '')
      .split(',')
      .map((ip) => ip.trim())
      .filter(Boolean);
    const payload: any = {
      tenant_id: this.tenantId,
      status: this.linkForm.status,
      expires_at: this.linkForm.expires_at || null,
      max_responses: this.linkForm.max_responses ? Number(this.linkForm.max_responses) : null,
      allow_anonymous: this.linkForm.allow_anonymous,
      allowed_ips
    };
    if (this.linkForm.password || this.removeLinkPassword) {
      payload.password = this.linkForm.password;
    }
    this.controlquestion.updateLink(this.activeLink.id, payload).subscribe((updated) => {
      this.activeLink = updated;
      this.generatedLink = `${window.location.origin}/controlquestion/public/${updated.token}`;
      this.editingLink = false;
      if (this.removeLinkPassword) {
        this.linkPasswordCache = '';
        this.persistLinkPassword(updated.id, '');
      } else if (this.linkForm.password) {
        this.linkPasswordCache = this.linkForm.password;
        this.persistLinkPassword(updated.id, this.linkPasswordCache);
      } else if (updated.password_preview) {
        this.linkPasswordCache = String(updated.password_preview || '');
        this.persistLinkPassword(updated.id, this.linkPasswordCache);
      } else if (!updated.has_password) {
        this.linkPasswordCache = '';
        this.persistLinkPassword(updated.id, '');
      }
      this.linkForm.password = '';
      this.removeLinkPassword = false;
      this.linkHasPassword = !!updated.has_password;
      this.linkPasswordVisible = false;
      this.loadSurveys();
    });
  }

  shouldShowMaskedLinkPassword() {
    return (
      this.linkForm.status === 'PUBLISHED' &&
      this.linkHasPassword &&
      !this.removeLinkPassword &&
      !this.linkForm.password
    );
  }

  getLinkPasswordFieldValue() {
    if (this.linkForm.password) {
      return this.linkForm.password;
    }
    if (this.linkPasswordVisible) {
      return this.normalizePasswordValue(this.linkPasswordCache);
    }
    return this.shouldShowMaskedLinkPassword() ? this.maskedPassword : '';
  }

  onLinkPasswordInput(value: string) {
    const text = String(value || '');
    if (this.shouldShowMaskedLinkPassword() && text === this.maskedPassword) {
      return;
    }
    this.linkForm.password = text;
    this.removeLinkPassword = false;
  }

  toggleLinkPasswordVisibility() {
    this.linkPasswordVisible = !this.linkPasswordVisible;
  }

  copyLinkPassword() {
    const value = this.normalizePasswordValue(this.linkForm.password) || this.normalizePasswordValue(this.linkPasswordCache);
    if (!value) {
      return;
    }
    this.copyTextToClipboard(value);
  }

  private persistLinkPassword(linkId: number, value: string) {
    if (!linkId) {
      return;
    }
    const key = `cq_link_password_${linkId}`;
    const normalized = this.normalizePasswordValue(value);
    if (!normalized) {
      localStorage.removeItem(key);
      return;
    }
    localStorage.setItem(key, normalized);
  }

  private readPersistedLinkPassword(linkId: number) {
    if (!linkId) {
      return '';
    }
    const key = `cq_link_password_${linkId}`;
    return this.normalizePasswordValue(localStorage.getItem(key) || '');
  }

  private normalizePasswordValue(value: string | null | undefined) {
    const normalized = String(value || '').trim();
    if (!normalized || normalized === this.maskedPassword) {
      return '';
    }
    return normalized;
  }

  async ensureSurveyHasQuestions(surveyId: number) {
    if (this.activeSurvey?.id === surveyId && this.questions.length) {
      return true;
    }
    if (!this.tenantId) {
      return false;
    }
    try {
      const detail = await firstValueFrom(this.controlquestion.getSurvey(this.tenantId, surveyId));
      if (!detail.questions?.length) {
        alert('Adicione pelo menos uma pergunta antes de gerar o link.');
        return false;
      }
      return true;
    } catch (_) {
      alert('Não foi possível verificar as perguntas do questionário.');
      return false;
    }
  }

  setSurveyStatus(survey: ControlQuestionSurvey, status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED', event?: Event) {
    event?.stopPropagation();
    if (!this.tenantId || !this.userId) {
      return;
    }
    this.openStatusMenuId = null;
    if (survey.link_id) {
      this.controlquestion
        .updateLink(survey.link_id, { tenant_id: this.tenantId, status })
        .subscribe((updated) => {
          this.surveys = this.surveys.map((item) =>
            item.id === survey.id
              ? {
                  ...item,
                  link_id: updated.id,
                  link_token: updated.token,
                  link_status: updated.status || null,
                  link_expires_at: updated.expires_at || null
                }
              : item
          );
        });
      return;
    }
    const payload = {
      tenant_id: this.tenantId,
      updated_by: this.userId,
      title: survey.title,
      description: survey.description || null,
      intro_text: survey.intro_text || null,
      outro_text: survey.outro_text || null,
      status,
      intranet_enabled: survey.intranet_enabled ? 1 : 0
    };
    this.controlquestion.updateSurvey(survey.id, payload).subscribe((updated) => {
      this.surveys = this.surveys.map((item) => (item.id === updated.id ? updated : item));
      if (this.activeSurvey?.id === updated.id) {
        this.activeSurvey = updated;
      }
    });
  }

  copyGeneratedLink() {
    if (!this.generatedLink) {
      return;
    }
    this.copyTextToClipboard(this.generatedLink);
  }

  openGeneratedLink() {
    if (!this.generatedLink) {
      return;
    }
    window.open(this.generatedLink, '_blank', 'noopener');
  }

  copySurveyLink(survey: ControlQuestionSurvey, event?: Event) {
    event?.stopPropagation();
    if (!survey.link_token) {
      this.copiedSurveyMessage = 'Link indisponível';
      this.copiedSurveyId = survey.id;
      if (this.copiedSurveyTimer) {
        clearTimeout(this.copiedSurveyTimer);
      }
      this.copiedSurveyTimer = setTimeout(() => {
        this.copiedSurveyId = null;
      }, 2000);
      return;
    }
    if (this.surveyDisplayStatus(survey) !== 'PUBLISHED') {
      this.copiedSurveyMessage = 'Link não publicado';
      this.copiedSurveyId = survey.id;
      if (this.copiedSurveyTimer) {
        clearTimeout(this.copiedSurveyTimer);
      }
      this.copiedSurveyTimer = setTimeout(() => {
        this.copiedSurveyId = null;
      }, 2000);
      return;
    }
    const link = `${window.location.origin}/controlquestion/public/${survey.link_token}`;
    this.copyTextToClipboard(link);
    this.copiedSurveyId = survey.id;
    this.copiedSurveyMessage = 'Link copiado!';
    if (this.copiedSurveyTimer) {
      clearTimeout(this.copiedSurveyTimer);
    }
    this.copiedSurveyTimer = setTimeout(() => {
      this.copiedSurveyId = null;
    }, 2000);
  }

  copyTextToClipboard(text: string) {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text);
      return;
    }
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
  }

  copyActiveLink(event?: Event) {
    event?.stopPropagation();
    if (!this.activeLink?.token) {
      return;
    }
    const link = `${window.location.origin}/controlquestion/public/${this.activeLink.token}`;
    this.copyTextToClipboard(link);
    this.linkCopied = true;
    if (this.linkCopiedTimer) {
      clearTimeout(this.linkCopiedTimer);
    }
    this.linkCopiedTimer = setTimeout(() => {
      this.linkCopied = false;
    }, 2000);
  }

  surveyDisplayStatus(survey: ControlQuestionSurvey) {
    return (survey.link_status || survey.status || '').toUpperCase();
  }

  formatDateTimeLocal(value: string) {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    const pad = (num: number) => String(num).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
      date.getHours()
    )}:${pad(date.getMinutes())}`;
  }

  openPreview() {
    this.previewAnswers = {};
    this.previewTouched = {};
    const token = this.activeSurvey?.link_token || this.activeLink?.token || '';
    if (token) {
      const previewUrl = `${window.location.origin}/controlquestion/public/${token}?autostart=1&preview=1&t=${Date.now()}`;
      this.previewFrameUrl = this.sanitizer.bypassSecurityTrustResourceUrl(previewUrl);
    } else {
      this.previewFrameUrl = null;
    }
    this.showPreviewModal = true;
  }

  openQuestionImagePicker() {
    this.questionImageInput?.nativeElement.click();
  }

  openQuestionVideoPicker() {
    this.questionVideoInput?.nativeElement.click();
  }

  handleQuestionMedia(event: Event, type: 'IMAGE' | 'VIDEO') {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      this.questionForm.media_type = type;
      this.questionForm.media_url = String(reader.result || '');
    };
    reader.readAsDataURL(file);
    input.value = '';
  }

  triggerOptionImage(index: number) {
    this.activeOptionImageIndex = index;
    const input = document.getElementById(`option-image-${index}`) as HTMLInputElement | null;
    input?.click();
  }

  handleOptionImage(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file == null || this.activeOptionImageIndex == null) {
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const next = [...this.optionDrafts];
      const target = next[this.activeOptionImageIndex!];
      if (target) {
        target.image_url = String(reader.result || '');
        if (!target.label) {
          target.label = file.name;
        }
      }
      this.optionDrafts = next;
      this.activeOptionImageIndex = null;
    };
    reader.readAsDataURL(file);
    input.value = '';
  }

  getOptionsForQuestion(questionId: number) {
    return this.options.filter((opt) => opt.question_id === questionId);
  }

  getMatrixRowsForQuestion(questionId: number) {
    return this.matrixRows.filter((row: any) => row.question_id === questionId);
  }

  getMatrixColsForQuestion(questionId: number) {
    return this.matrixCols.filter((col: any) => col.question_id === questionId);
  }

  getPreviewRatingOptions(question: ControlQuestion) {
    const options = this.getOptionsForQuestion(question.id);
    if (!['STAR', 'SCALE'].includes(question.question_type)) {
      return options;
    }
    return [...options].sort((a, b) => this.getStarCount(a) - this.getStarCount(b));
  }

  getPreviewSelectedRatingScore(questionId: number) {
    const selectedId = Number(this.previewAnswers[questionId] || 0);
    if (!selectedId) {
      return 0;
    }
    const selected = this.getOptionsForQuestion(questionId).find((option) => option.id === selectedId);
    return selected ? this.getStarCount(selected) : 0;
  }

  selectPreviewRatingScore(questionId: number, score: number) {
    const option = this.getPreviewOptionByScore(questionId, score);
    if (!option) {
      return;
    }
    this.previewAnswers[questionId] = option.id;
    this.previewTouched[questionId] = true;
  }

  getPreviewEmojiSymbol(label?: string | null) {
    const text = String(label || '').trim();
    if (!text) {
      return '🙂';
    }
    const firstToken = text.split(/\s+/)[0];
    const looksLikeEmoji = /[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}]/u.test(firstToken);
    return looksLikeEmoji ? firstToken : '🙂';
  }

  getPreviewEmojiText(label?: string | null) {
    const text = String(label || '').trim();
    if (!text) {
      return '';
    }
    const firstToken = text.split(/\s+/)[0];
    const looksLikeEmoji = /[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}]/u.test(firstToken);
    return looksLikeEmoji ? text.replace(firstToken, '').trim() : text;
  }

  getPreviewOptionDisplayText(option: ControlQuestionOption) {
    const label = String(option?.label || '').trim();
    if (!label || this.isLikelyFileName(label)) {
      return '';
    }
    return label;
  }

  togglePreviewMulti(questionId: number, optionId: number) {
    const current = Array.isArray(this.previewAnswers[questionId]) ? [...this.previewAnswers[questionId]] : [];
    const idx = current.indexOf(optionId);
    if (idx >= 0) {
      current.splice(idx, 1);
    } else {
      current.push(optionId);
    }
    this.previewAnswers[questionId] = current;
    this.previewTouched[questionId] = true;
  }

  markPreviewTouched(questionId: number) {
    this.previewTouched[questionId] = true;
  }

  isPreviewAnswered(question: ControlQuestion) {
    const value = this.previewAnswers[question.id];
    if (question.question_type === 'MULTIPLE_CHOICE') {
      return Array.isArray(value) && value.length > 0;
    }
    if (['TEXT', 'CUSTOM_TEXT', 'EMAIL'].includes(question.question_type)) {
      return typeof value === 'string' && value.trim().length > 0;
    }
    if (['NUMBER', 'NPS', 'SCALE', 'DATE'].includes(question.question_type)) {
      return value !== undefined && value !== null && value !== '';
    }
    return value !== undefined && value !== null && value !== '';
  }

  shouldShowRequiredFeedback(question: ControlQuestion) {
    return !!question.required && !!this.previewTouched[question.id] && !this.isPreviewAnswered(question);
  }

  private getPreviewOptionByScore(questionId: number, score: number) {
    const normalized = Math.max(1, Math.min(5, score));
    const options = [...this.getOptionsForQuestion(questionId)].sort((a, b) => this.getStarCount(a) - this.getStarCount(b));
    const exact = options.find((option) => this.getStarCount(option) === normalized);
    if (exact) {
      return exact;
    }
    return options[normalized - 1] || options[0] || null;
  }

  private isLikelyFileName(value: string) {
    const text = String(value || '').trim().toLowerCase();
    if (!text) {
      return false;
    }
    return /\.(png|jpe?g|gif|webp|svg|bmp|heic|heif|mp4|webm|mov|avi|mkv|mpeg|mpg|m4v)$/i.test(text);
  }

  async reorderQuestions(event: CdkDragDrop<ControlQuestion[]>) {
    if (!this.tenantId || !this.userId) {
      return;
    }
    moveItemInArray(this.questions, event.previousIndex, event.currentIndex);
    const updates = this.questions.map((question, index) => {
      const payload = {
        tenant_id: this.tenantId,
        question_type: question.question_type,
        title: question.title,
        description: question.description || null,
        media_type: question.media_type || 'NONE',
        media_url: question.media_url || null,
        weight: question.weight || 0,
        required: question.required ? 1 : 0,
        display_format: question.display_format || 'ROW',
        min_answers: question.min_answers || 0,
        max_answers: question.max_answers || 1,
        order_index: index,
        updated_by: this.userId
      };
      return firstValueFrom(this.controlquestion.updateQuestion(question.id, payload));
    });
    await Promise.all(updates);
  }
}
